'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { preprocessNumbersForSpeech, getTimeBasedGreeting } from '@/lib/speechUtils';

/**
 * Global audio player hook using browser SpeechSynthesis API
 * Reads text from storage/news/ data and converts to speech
 * Ensures only one audio plays at a time across all news cards
 * 
 * Features:
 * - Time-based greetings (morning/afternoon/evening)
 * - Smart number narration (converts numbers to words)
 * - Smooth playback (cancels previous speech before starting new)
 * - Autoplay restriction handling
 * - Two-person conversation support with different voices
 */
type AudioMode = 'card' | 'global' | null;

interface ConversationSegment {
  speaker: string;
  text: string;
}

interface PlayOptions {
  mode?: AudioMode;
  withGreeting?: boolean;
  customGreeting?: string; // Custom greeting message (overrides time-based greeting if provided)
  preprocessNumbers?: boolean;
  isConversation?: boolean; // Enable two-person conversation mode
  segments?: ConversationSegment[]; // Pre-parsed conversation segments
}

export function useAudioPlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentText, setCurrentText] = useState<string | null>(null);
  const [audioMode, setAudioMode] = useState<AudioMode>(null);
  const [currentSpeaker, setCurrentSpeaker] = useState<string | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const synthRef = useRef<typeof window.speechSynthesis | null>(null);
  const isInitializedRef = useRef(false);
  const pendingPlayRef = useRef<{ text: string; options: PlayOptions } | null>(null);
  const conversationQueueRef = useRef<ConversationSegment[]>([]);
  const isConversationModeRef = useRef(false);
  const conversationVoicesRef = useRef<{ sarah: SpeechSynthesisVoice | null; mike: SpeechSynthesisVoice | null }>({ sarah: null, mike: null });

  // Initialize speech synthesis and load voices
  // Handle browser autoplay restrictions by requiring user interaction
  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      synthRef.current = window.speechSynthesis;
      
      // Load voices (some browsers need this)
      const loadVoices = () => {
        if (synthRef.current) {
          synthRef.current.getVoices();
        }
      };
      
      // Load voices immediately
      loadVoices();
      
      // Some browsers load voices asynchronously
      if ('onvoiceschanged' in synthRef.current) {
        synthRef.current.onvoiceschanged = loadVoices;
      }
      
      // Mark as initialized after a short delay to allow voices to load
      // This helps with autoplay restrictions
      const initTimer = setTimeout(() => {
        isInitializedRef.current = true;
        
        // If there's a pending play request, execute it now
        if (pendingPlayRef.current) {
          const { text, options } = pendingPlayRef.current;
          pendingPlayRef.current = null;
          // Use a small delay to ensure voices are ready
          setTimeout(() => {
            internalPlay(text, options);
          }, 100);
        }
      }, 500);
      
      return () => clearTimeout(initTimer);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (synthRef.current) {
        synthRef.current.cancel();
      }
    };
  }, []);

  /**
   * Select two distinct voices for SARAH (female) and MIKE (male).
   * Uses name heuristics — different browsers/OS use different voice names.
   * Guarantees we never use the same voice for both speakers.
   */
  const selectConversationVoices = useCallback((voices: SpeechSynthesisVoice[]): void => {
    const en = voices.filter((v) => v.lang.startsWith('en'));
    if (en.length === 0) {
      conversationVoicesRef.current = { sarah: null, mike: null };
      return;
    }

    const name = (v: SpeechSynthesisVoice) => v.name.toLowerCase();

    // Female candidates: common names across Windows, Mac, Chrome, Edge
    const femalePatterns = [
      'samantha', 'karen', 'victoria', 'zira', 'susan', 'linda', 'laura', 'michelle',
      'emily', 'emma', 'female', 'woman', 'girl', 'alice', 'kate', 'moira',
      'sarah', 'anna', 'fiona', 'tessa', 'google uk english female', 'microsoft zira',
      'english female', 'us english female', 'uk english female'
    ];
    const sarahVoice =
      en.find((v) => femalePatterns.some((p) => name(v).includes(p))) ||
      null;

    // Male candidates: exclude the chosen female so we always pick a different voice
    const malePatterns = [
      'daniel', 'alex', 'james', 'david', 'mark', 'paul', 'george', 'fred', 'ralph',
      'male', 'man', 'boy', 'tom', 'bruce', 'aaron', 'evan', 'nathan', 'ryan',
      'google uk english male', 'microsoft david', 'english male', 'us english male',
      'uk english male'
    ];
    const remaining = sarahVoice ? en.filter((v) => v !== sarahVoice) : en;
    const mikeVoice =
      remaining.find((v) => malePatterns.some((p) => name(v).includes(p))) ||
      null;

    // If we have only one match, use it for one speaker and pick any OTHER voice for the other
    if (sarahVoice && !mikeVoice) {
      const other = en.find((v) => v !== sarahVoice);
      conversationVoicesRef.current = { sarah: sarahVoice, mike: other || null };
      return;
    }
    if (!sarahVoice && mikeVoice) {
      const other = en.find((v) => v !== mikeVoice);
      conversationVoicesRef.current = { sarah: other || null, mike: mikeVoice };
      return;
    }
    if (sarahVoice && mikeVoice) {
      conversationVoicesRef.current = { sarah: sarahVoice, mike: mikeVoice };
      return;
    }

    // No gendered match: use first two distinct voices so they still sound different
    conversationVoicesRef.current = {
      sarah: en[0] ?? null,
      mike: en.length > 1 ? en[1] : null
    };
  }, []);

  // Play next segment in conversation queue
  const playNextConversationSegment = useCallback(() => {
    if (!synthRef.current || conversationQueueRef.current.length === 0) {
      // Conversation finished
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentSpeaker(null);
      isConversationModeRef.current = false;
      return;
    }

    const segment = conversationQueueRef.current.shift()!;
    const { sarah, mike } = conversationVoicesRef.current;
    const voice = segment.speaker === 'SARAH' ? sarah : mike;

    // Preprocess text
    const processedText = preprocessNumbersForSpeech(segment.text);

    const utterance = new SpeechSynthesisUtterance(processedText);

    // Use distinct voice when available
    if (voice) {
      utterance.voice = voice;
    }

    // Pitch/rate: use bigger gap when we have only one voice so they still sound different
    const hasTwoVoices = sarah && mike && sarah !== mike;
    if (segment.speaker === 'SARAH') {
      utterance.rate = hasTwoVoices ? 1.0 : 1.05;
      utterance.pitch = hasTwoVoices ? 1.15 : 1.35; // Higher for Sarah
    } else {
      utterance.rate = hasTwoVoices ? 0.95 : 0.9;
      utterance.pitch = hasTwoVoices ? 0.9 : 0.7;  // Lower for Mike; wider gap if single voice
    }
    utterance.volume = 1.0;

    utterance.onend = () => {
      setTimeout(() => playNextConversationSegment(), 300);
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      playNextConversationSegment();
    };

    utteranceRef.current = utterance;
    setCurrentSpeaker(segment.speaker);
    setIsPlaying(true);

    try {
      synthRef.current.speak(utterance);
    } catch (error) {
      console.warn('Speech synthesis error:', error);
      playNextConversationSegment();
    }
  }, []);

  // Play conversation (multiple segments with different voices)
  const playConversation = useCallback((
    segments: ConversationSegment[],
    mode: AudioMode,
    originalText: string
  ) => {
    if (!synthRef.current || segments.length === 0) return;

    // Cancel any current playback
    synthRef.current.cancel();

    // Select and cache two distinct voices (Sarah vs Mike) before starting
    const voices = synthRef.current.getVoices();
    selectConversationVoices(voices);

    // Set up conversation queue
    conversationQueueRef.current = [...segments];
    isConversationModeRef.current = true;

    // Update state
    setCurrentText(originalText);
    setAudioMode(mode);
    setIsPlaying(true);
    setIsPaused(false);

    // Start playing first segment
    playNextConversationSegment();
  }, [playNextConversationSegment, selectConversationVoices]);

  // Internal play function that handles the actual speech synthesis
  const continuePlay = useCallback((
    text: string,
    mode: AudioMode,
    withGreeting: boolean,
    customGreeting: string | undefined,
    preprocessNumbers: boolean
  ) => {
    if (!synthRef.current) return;

    // Preprocess text: convert numbers to words
    let processedText = text;
    if (preprocessNumbers) {
      processedText = preprocessNumbersForSpeech(text);
    }

    // Add greeting if requested
    if (withGreeting) {
      // Use custom greeting if provided, otherwise use time-based greeting
      const greeting = customGreeting || getTimeBasedGreeting();
      processedText = `${greeting}. ${processedText}`;
    }

    // Create new utterance with human-like voice settings
    const utterance = new SpeechSynthesisUtterance(processedText);
    
    // Configure for natural, human-like speech (optimized for conversational tone)
    utterance.rate = 1.05; // Slightly faster for more natural flow (human speech is typically 1.0-1.2)
    utterance.pitch = 1.0; // Normal pitch for natural, human-like sound
    utterance.volume = 1.0; // Full volume for clear audio
    
    // Select the most human-like voice available
    // Priority: Neural voices (most natural) > Natural/Premium > High-quality standard voices
    const voices = synthRef.current.getVoices();
    
    const preferredVoice = 
      // 1. Neural/Neural2 voices - most natural, human-like voices (Edge, Chrome)
      voices.find((voice) => 
        voice.lang.startsWith('en') && 
        (voice.name.includes('Neural') || 
         voice.name.includes('Natural') ||
         voice.name.includes('Premium'))
      ) ||
      // 2. High-quality voices known for natural sound
      voices.find((voice) => 
        voice.lang.startsWith('en') && 
        (voice.name.includes('Google') ||
         voice.name.includes('Microsoft') ||
         voice.name.includes('Samantha') ||
         voice.name.includes('Alex') ||
         voice.name.includes('Karen') ||
         voice.name.includes('Daniel'))
      ) ||
      // 3. Fallback to US English, then GB English, then any English
      voices.find((voice) => voice.lang.startsWith('en-US')) ||
      voices.find((voice) => voice.lang.startsWith('en-GB')) ||
      voices.find((voice) => voice.lang.startsWith('en')) ||
      voices[0];
    
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }

    // Set up event handlers
    utterance.onend = () => {
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentText(null);
      setAudioMode(null);
      setCurrentSpeaker(null);
      utteranceRef.current = null;
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentText(null);
      setAudioMode(null);
      setCurrentSpeaker(null);
      utteranceRef.current = null;
    };

    // Store reference and play
    utteranceRef.current = utterance;
    setCurrentText(text); // Store original text for comparison
    setAudioMode(mode);
    setIsPlaying(true);
    setIsPaused(false);
    
    // Handle autoplay restrictions gracefully
    try {
      synthRef.current.speak(utterance);
    } catch (error) {
      console.warn('Speech synthesis error (may be due to autoplay restrictions):', error);
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentText(null);
      setAudioMode(null);
      setCurrentSpeaker(null);
      utteranceRef.current = null;
    }
  }, []);

  // Internal play function that handles the actual speech synthesis
  const internalPlay = useCallback((text: string, options: PlayOptions = {}) => {
    if (!synthRef.current) {
      console.warn('SpeechSynthesis not supported in this browser');
      return;
    }

    const {
      mode = 'card',
      withGreeting = false,
      customGreeting,
      preprocessNumbers = true,
      isConversation = false,
      segments = []
    } = options;

    // Always cancel any currently playing or pending speech
    // This ensures smooth playback without overlapping
    if (synthRef.current.speaking || synthRef.current.pending) {
      synthRef.current.cancel();
      conversationQueueRef.current = []; // Clear conversation queue
      isConversationModeRef.current = false;
    }

    // Handle conversation mode
    if (isConversation && segments.length > 0) {
      setTimeout(() => {
        playConversation(segments, mode, text);
      }, 50);
    } else {
      setTimeout(() => {
        continuePlay(text, mode, withGreeting, customGreeting, preprocessNumbers);
      }, 50);
    }
  }, [continuePlay, playConversation]);

  // Public play function with options support
  const play = useCallback((text: string, modeOrOptions?: AudioMode | PlayOptions, options?: PlayOptions) => {
    if (!synthRef.current) {
      console.warn('SpeechSynthesis not supported in this browser');
      return;
    }

    // Handle backward compatibility: play(text, mode) or play(text, options)
    let finalOptions: PlayOptions;
    if (typeof modeOrOptions === 'string' || modeOrOptions === null) {
      // Old API: play(text, mode)
      finalOptions = { mode: modeOrOptions || 'card', ...options };
    } else {
      // New API: play(text, options)
      finalOptions = modeOrOptions || {};
    }

    // If not initialized yet, queue the play request
    if (!isInitializedRef.current) {
      pendingPlayRef.current = { text, options: finalOptions };
      return;
    }

    internalPlay(text, finalOptions);
  }, [internalPlay]);

  const pause = useCallback(() => {
    if (synthRef.current && isPlaying && !isPaused) {
      synthRef.current.pause();
      setIsPaused(true);
      setIsPlaying(false);
    }
  }, [isPlaying, isPaused]);

  const resume = useCallback(() => {
    if (synthRef.current && isPaused && utteranceRef.current) {
      synthRef.current.resume();
      setIsPaused(false);
      setIsPlaying(true);
    }
  }, [isPaused]);

  const stop = useCallback(() => {
    if (synthRef.current) {
      synthRef.current.cancel();
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentText(null);
      setAudioMode(null);
      setCurrentSpeaker(null);
      utteranceRef.current = null;
      conversationQueueRef.current = [];
      isConversationModeRef.current = false;
    }
  }, []);

  const toggle = useCallback((text: string, modeOrOptions?: AudioMode | PlayOptions, options?: PlayOptions) => {
    // Handle backward compatibility
    let finalOptions: PlayOptions;
    if (typeof modeOrOptions === 'string' || modeOrOptions === null) {
      finalOptions = { mode: modeOrOptions || 'card', ...options };
    } else {
      finalOptions = modeOrOptions || {};
    }

    const mode = finalOptions.mode || 'card';

    if (isPlaying && currentText === text && audioMode === mode) {
      // If same text is playing in same mode, pause it
      pause();
    } else if (isPaused && currentText === text && audioMode === mode) {
      // If same text is paused in same mode, resume it
      resume();
    } else if ((isPlaying || isPaused) && (currentText !== text || audioMode !== mode)) {
      // If different text or mode is playing/paused, stop and play new one
      stop();
      // Small delay to ensure cancellation is complete
      setTimeout(() => play(text, finalOptions), 100);
    } else {
      // Not playing, start playing
      play(text, finalOptions);
    }
  }, [isPlaying, isPaused, currentText, audioMode, play, pause, resume, stop]);

  return {
    isPlaying,
    isPaused,
    currentText,
    audioMode,
    currentSpeaker,
    play,
    pause,
    resume,
    stop,
    toggle,
  };
}
